import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, FloppyDisk, ArrowUp, ArrowDown } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";
import { DEFAULT_SLAB_OPTIONS, resetSlabOptions } from "@/lib/slabOptions";

const LISTS = [
  {
    key: "materials",
    title: "Materials",
    singular: "material",
    hint: "What the slab is made of — shown in the Material dropdown when adding or editing a slab.",
    placeholder: "e.g. Dolomite",
  },
  {
    key: "thicknesses",
    title: "Thicknesses",
    singular: "thickness",
    hint: "Slab thickness choices for slabs and remnants.",
    placeholder: "e.g. 1.2cm",
  },
];

const OptionList = ({ def, items, onChange }) => {
  const upd = (i, v) => onChange(items.map((x, j) => (j === i ? v : x)));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <section
      className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
      data-testid={`slab-options-${def.key}`}
    >
      <h2 className="text-base font-semibold md:text-lg">{def.title}</h2>
      <p className="mt-1 text-xs text-zinc-500">{def.hint}</p>
      <div className="mt-3 space-y-2">
        {items.map((v, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`slab-option-row-${def.key}-${i}`}>
            <div className="flex flex-col">
              <button
                type="button"
                aria-label="Move up"
                data-testid={`slab-option-up-${def.key}-${i}`}
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="text-zinc-500 hover:text-zinc-200 disabled:opacity-30"
              >
                <ArrowUp size={12} weight="bold" />
              </button>
              <button
                type="button"
                aria-label="Move down"
                data-testid={`slab-option-down-${def.key}-${i}`}
                onClick={() => move(i, 1)}
                disabled={i === items.length - 1}
                className="text-zinc-500 hover:text-zinc-200 disabled:opacity-30"
              >
                <ArrowDown size={12} weight="bold" />
              </button>
            </div>
            <Input
              data-testid={`slab-option-input-${def.key}-${i}`}
              value={v}
              onChange={(e) => upd(i, e.target.value)}
              placeholder={def.placeholder}
              className="h-10 flex-1 border-zinc-800 bg-zinc-950"
            />
            <button
              type="button"
              aria-label="Remove"
              data-testid={`slab-option-remove-${def.key}-${i}`}
              onClick={() => onChange(items.filter((_, j) => j !== i))}
              className="text-zinc-500 transition-colors duration-150 hover:text-red-400"
            >
              <Trash size={16} weight="bold" />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <p className="rounded-md border border-dashed border-zinc-800 p-4 text-center text-xs text-zinc-500">
            Empty — the built-in list is used until you add one.
          </p>
        )}
      </div>
      <Button
        type="button"
        variant="outline"
        data-testid={`slab-option-add-${def.key}`}
        onClick={() => onChange([...items, ""])}
        className="mt-3 border-zinc-700 bg-transparent"
      >
        <Plus size={14} weight="bold" className="mr-1" /> Add {def.singular}
      </Button>
    </section>
  );
};

export default function SlabOptionsSettings() {
  const [opts, setOpts] = useState(DEFAULT_SLAB_OPTIONS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/settings/slab-options")
      .then(({ data }) => setOpts({ ...DEFAULT_SLAB_OPTIONS, ...data }))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/slab-options", {
        materials: opts.materials.map((s) => s.trim()).filter(Boolean),
        thicknesses: opts.thicknesses.map((s) => s.trim()).filter(Boolean),
      });
      setOpts({ ...DEFAULT_SLAB_OPTIONS, ...data });
      resetSlabOptions();
      toast.success("Slab options saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Slab Materials & Thicknesses" />
      <main className="relative z-10 mx-auto max-w-2xl px-4 pb-20 pt-6">
        <div className="text-xs uppercase tracking-widest text-zinc-600">Settings</div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl" data-testid="slab-options-title">
          Slab Materials &amp; Thicknesses
        </h1>
        <p className="mt-1 text-sm text-zinc-500">
          The pick-lists the Slab Inventory uses. Slabs already saved with a value you remove keep
          it and still show it in their dropdown.
        </p>
        <div className="mt-6 space-y-4">
          {LISTS.map((def) => (
            <OptionList
              key={def.key}
              def={def}
              items={opts[def.key] || []}
              onChange={(list) => setOpts((o) => ({ ...o, [def.key]: list }))}
            />
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <Button
            type="button"
            data-testid="slab-options-save"
            onClick={save}
            disabled={saving}
            className="bg-indigo-500 hover:bg-indigo-400"
          >
            <FloppyDisk size={14} weight="bold" className="mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </main>
    </div>
  );
}
